import AsyncRecorder from './AsyncRecorder';
import { MAX_LAMBDA_VIDEO_SIZE, VIDEO_FPS } from '../data/Config';
import {
  generateConversionParams,
  MediaConvertNameModifier
} from '../utils/AWSConfig';
import {
  deleteFile,
  getBaseURL,
  getAvailableFilenameWithExtension,
  uploadFileCancellable,
  renameFile,
  convertVideo
} from '../utils/S3Utils';
import { compressAudioFile, formatBytes } from '../utils';
import { ManagedUpload } from 'aws-sdk/clients/s3';
import { waitForJobCompletion } from '../utils/AWSMediaConvertUtils';
import RecordType from '../data/RecordType';
import { RecorderEvent } from './Recorder';
import FileType from '../data/FileType';
import { getStoreState } from '../state/Store';
import { reportBFError } from 'utils/Tracker';
import { callFunction } from 'utils/FirebaseUtils';
import { isFigmaPro } from 'state/AppState';

const toWav = require('audiobuffer-to-wav');
const WebMWriter = require('webm-writer');

export class BrowserAsyncRecorder extends AsyncRecorder {
  audioSource: AudioBuffer | string | null;
  videoWriter: any;
  type = RecordType.VideoAsync;

  private fileId: string;
  private videoUrl: string;
  private jobId: string | undefined;
  private hasUploadedAudio = false;
  private hasUploadedVideo = false;
  private needsToUploadAudio = false;
  private audioUploadRequest: ManagedUpload | undefined = undefined;
  private videoUploadRequest: ManagedUpload | undefined = undefined;
  private audioUploadProgress = 0;
  private videoUploadProgress = 0;
  private isBeatflyerLite: boolean;

  constructor(
    canvas: HTMLCanvasElement,
    framesNumber: number,
    duration: number,
    audioSource: AudioBuffer | string | null,
    offlineFrequencies: Uint8Array[] | null,
    offlineWaveforms: Uint8Array[] | null
  ) {
    super(canvas, framesNumber, duration, offlineFrequencies, offlineWaveforms);

    this.audioSource = audioSource;
    this.videoWriter = new WebMWriter({
      quality: 0.7,
      frameRate: VIDEO_FPS
    });
    const { app } = getStoreState();
    const { isBeatflyerLite, isCloudRenderer } = app;
    this.isBeatflyerLite =
      (isBeatflyerLite || isCloudRenderer) && !isFigmaPro(app);
    this.needsToUploadAudio =
      !this.isBeatflyerLite &&
      this.audioSource !== null &&
      typeof this.audioSource === 'object';
    console.log('Needs to upload audio? ' + this.needsToUploadAudio);
    if (!this.isBeatflyerLite) {
      this.getFileId();
    }
  }

  async getFileId() {
    this.fileId = await getAvailableFilenameWithExtension('webm');
    console.log('File id: ' + this.fileId);
    // start uploading audio to S3
    // if the file wasn't edited, perhaps we could simply pass to the converted a URL?
    console.log('AUDIO SOURCE: ' + typeof this.audioSource);
    if (this.needsToUploadAudio) {
      this.uploadAudio();
    }
  }

  async uploadAudio() {
    const wav = toWav(this.audioSource);
    const file = new File([wav], this.fileId + '.wav');
    const compressedFile = await compressAudioFile(file);
    const filename = this.fileId + '.mp3';
    this.audioUploadRequest = uploadFileCancellable(
      filename,
      compressedFile,
      'audio/mp3',
      () => {
        console.log('Uploaded audio');
        this.audioUploadRequest = undefined;
        this.audioSource = getBaseURL() + filename;
        this.hasUploadedAudio = true;
        this.didUpload(0);
      },
      (error: string) => {
        if (!this.hasCancelled) {
          reportBFError(new Error(error));
          alert(error);
        }
      },
      (progress: number) => {
        this.audioUploadProgress = progress;
        this.emitUploadProgress();
      }
    );
  }

  update() {
    if (this._currentFrame === this.framesNumber) {
      return;
    }
    this.videoWriter.addFrame(this.canvas);
    this._currentFrame++;
    if (this._currentFrame === this.framesNumber) {
      this.emit(RecorderEvent.RecordingEnded);
      this.videoWriter.complete().then((webMBlob: Blob) => {
        console.log(
          `Recorded ${formatBytes(webMBlob.size)} webm video long ${
            this.duration / 1000
          }s with BrowserAsyncRecorder`
        );
        if (this.isBeatflyerLite) {
          this.emit(RecorderEvent.Complete, URL.createObjectURL(webMBlob));
          return;
        }
        this.uploadVideo(webMBlob);
      });
    }
  }

  async uploadVideo(blob: Blob) {
    console.log('Upload Video');
    // upload video
    const filename = this.fileId + '.webm';
    const file = new File([blob], filename);
    this.videoUploadRequest = uploadFileCancellable(
      filename,
      file,
      FileType.webm,
      () => {
        console.log('Uploaded video');
        this.videoUploadRequest = undefined;
        this.videoUrl = getBaseURL() + filename;
        this.hasUploadedVideo = true;
        this.didUpload(file.size);
      },
      (error: string) => {
        if (!this.hasCancelled) {
          reportBFError(new Error(error));
          alert(error);
        }
      },
      (progress: number) => {
        this.videoUploadProgress = progress;
        this.emitUploadProgress();
      }
    );
  }

  async converVideoWithMediaConvert() {
    const params = generateConversionParams(
      this.videoUrl,
      this.audioSource as string
    );
    this.jobId = (await callFunction('createMediaConvertJob', params)).data;
    console.log('Job Id ' + this.jobId);
    if (!this.jobId) {
      throw new Error('Could not create conversion job');
    }
    const success = await waitForJobCompletion(this.jobId);
    const oldName = `${this.fileId}${MediaConvertNameModifier}.mp4`;
    const newName = `${this.fileId}.mp4`;
    await renameFile(oldName, newName);
    if (success && !this.hasCancelled) {
      this.emit(RecorderEvent.Complete, getBaseURL() + newName);
    }
  }

  async convertVideoWithLambdaFunction() {
    const newVideoUrl = await convertVideo(
      this.videoUrl,
      this.audioSource as string
    );
    if (!this.hasCancelled) {
      this.emit(RecorderEvent.Complete, newVideoUrl);
    }
  }

  async didUpload(filesize: number) {
    if (this.hasCancelled) {
      return;
    }
    if (
      (!this.audioSource || typeof this.audioSource === 'string') &&
      this.videoUrl
    ) {
      this.emit(RecorderEvent.ConversionProgressed, undefined);
      try {
        if (filesize > MAX_LAMBDA_VIDEO_SIZE) {
          await this.converVideoWithMediaConvert();
        } else {
          await this.convertVideoWithLambdaFunction();
        }
      } catch (e) {
        if (!this.hasCancelled) {
          alert(e);
        }
      }
      this.deleteUploadedAssets();
    }
  }

  emitUploadProgress() {
    if (this.getPosition() < 1) {
      return;
    }
    let progress = this.videoUploadProgress;
    if (this.needsToUploadAudio) {
      progress += this.audioUploadProgress;
      progress /= 2;
    }
    this.emit(RecorderEvent.UploadProgressed, progress);
  }

  abortUploadRequests = () => {
    if (this.audioUploadRequest) {
      this.audioUploadRequest.abort();
    }
    if (this.videoUploadRequest) {
      this.videoUploadRequest.abort();
    }
  };

  deleteUploadedAssets = async () => {
    if (this.hasUploadedAudio) {
      await deleteFile(this.fileId + '.mp3');
    }
    if (this.hasUploadedVideo) {
      await deleteFile(this.fileId + '.webm');
    }
  };

  cancel() {
    super.cancel();

    this.abortUploadRequests();
    this.deleteUploadedAssets();
    if (this.jobId) {
      // cancel conversion
      callFunction('cancelMediaConvertJob', { jobId: this.jobId });
    } else {
      // cancel recording
      this.videoWriter.complete().then((webMBlob: Blob) => {
        //
      });
    }
  }
}



// WEBPACK FOOTER //
// ./src/video/BrowserAsyncRecorder.ts